import { orderBy } from 'lodash-es'
import * as codeSystems from '#src/codeSystems.ts'
import {
  ExtendedMedicationRequest,
  medicationLabelForGroup,
} from '#src/models/medicationRequest.ts'

export interface GroupedMedication {
  // Group representative; also supplies medicationDisplay and doseSummary.
  id?: string
  medicationCode?: string
  medicationDisplay: string
  type?: string
  startDate?: string
  lastOrderedDate?: string
  medicationRequests: ExtendedMedicationRequest[]
  latestStatus?: string
  // Elation marks a medication thread non-permanent for one-time and
  // short-course orders (a clinic dose, a 5-day antibiotic). Those are the
  // "temporary" medications, as opposed to the ones a participant stays on.
  isTemporary: boolean
  doseSummary?: string
}

export interface PartitionedMedications {
  rxMedications: GroupedMedication[]
  otcMedications: GroupedMedication[]
  activeRxMedications: GroupedMedication[]
  activeOtcMedications: GroupedMedication[]
  temporaryRxMedications: GroupedMedication[]
  temporaryOtcMedications: GroupedMedication[]
  discontinuedMedications: GroupedMedication[]
}

function isOtcMedication(medication: GroupedMedication): boolean {
  return medication.type === codeSystems.MedicationRequestType.OverTheCounter
}

/**
 * One row per medication group, newest order first: the rows Orbit's
 * medication card and the facesheet PDF list.
 *
 * @param medicationRequests - All of a participant's medication requests
 * @param options - Grouping options, passed to `groupMedications`
 * @param options.groupByElationThread - Group by Elation thread id
 * @returns The grouped rows, sorted by the representative's effective date
 */
export function groupCurrentMedications(
  medicationRequests: ExtendedMedicationRequest[],
  options: { groupByElationThread?: boolean } = {}
): GroupedMedication[] {
  const medications = ExtendedMedicationRequest.groupMedications(
    medicationRequests,
    options
  ).map((group): GroupedMedication => {
    const earliest = group.requests[group.requests.length - 1]
    const representative = group.representativeRequest
    const label = medicationLabelForGroup(group)

    return {
      id: representative.id,
      medicationCode: representative.medicationCode,
      medicationDisplay: label.display,
      type: representative.type,
      startDate: earliest?.effectiveDate,
      lastOrderedDate: representative.effectiveDate,
      medicationRequests: group.requests,
      latestStatus: group.currentStatus,
      isTemporary: representative.isPermanent === false,
      doseSummary: label.doseSummary,
    }
  })

  return orderBy(
    medications,
    (m) => (m.lastOrderedDate ? new Date(m.lastOrderedDate).getTime() : 0),
    ['desc']
  )
}

export function partitionMedications(
  medications: GroupedMedication[]
): PartitionedMedications {
  // A non-permanent thread is synced as `completed` once its single dose is
  // given, so temporary medications are collected before the active filter,
  // not after it. rx/otc keep every active group, temporary ones included,
  // so the ungrouped card renders exactly what it always did.
  //
  // Stopped and cancelled groups go to Discontinued rather than Temporary:
  // those were discontinued or never dispensed, so listing them as Temporary
  // would present a historical order as current and count it in the card's
  // badge.
  const temporary = medications.filter(
    (m) =>
      m.isTemporary &&
      (m.latestStatus === 'active' || m.latestStatus === 'completed')
  )
  const currentlyActive = medications.filter((m) => m.latestStatus === 'active')
  const active = currentlyActive.filter((m) => !m.isTemporary)
  const discontinued = medications.filter(
    (m) => m.latestStatus === 'stopped' || m.latestStatus === 'cancelled'
  )

  return {
    rxMedications: currentlyActive.filter(
      (m) => m.type === codeSystems.MedicationRequestType.Prescription
    ),
    otcMedications: currentlyActive.filter(isOtcMedication),
    activeRxMedications: active.filter((m) => !isOtcMedication(m)),
    activeOtcMedications: active.filter(isOtcMedication),
    temporaryRxMedications: temporary.filter((m) => !isOtcMedication(m)),
    temporaryOtcMedications: temporary.filter(isOtcMedication),
    discontinuedMedications: discontinued,
  }
}
